/*
    The main application object
    Implements the main application logic, map state, map objects and context
 */
import {Evented} from "@/engine/evented.js";
import * as L from 'leaflet';
import {elog, log, profiling, profilingFinish, profilingStart} from "@/engine/helpers/logging.js";
import {identity, mapApi, mapContext, shortLivedTokenManager, sm} from "@/context/global.js";
import {IDB} from "@/engine/db.js";
import {UA} from "@/engine/helpers/UA.js";
import {gcUtils} from "@/engine/helpers/gcUtils.js";
import {gpsStateMachine} from "@/engine/gps/gpsStateMachine.js";
import {locationLoadFromBrowserUrl, optionsFromBrowserUrl, updateBrowserUrl} from "@/engine/helpers/HLocationURI.js";
import {statCollector} from "@/engine/helpers/statCollector.js";
import {geometryUtils} from "@/engine/helpers/geometryUtils.js";
import {LAYER_TYPE_RASTER, Workspace} from "@/engine/workspace.js";
import {appContext} from "@/engine/appContext.js";
import {UIWorkspaceBounds} from "@/engine/UIWorkspaceBounds.js";
import {MapUI} from "@/engine/mapUI.js";
import {AMUtil} from "@/engine/leaflet/antimeridianUtils.js";
import "leaflet.pdf"

import {
    sseCM,
    CM_TYPE_PROGRESS,
    CM_TYPE_WORKSPACE_DELETED,
    CM_TYPE_WORKSPACE_CREATED,
    CM_TYPE_LAYER_UPDATE,
    CM_TYPE_LAYER_CREATED,
    CM_ERROR_FORMAT,
    CM_ERROR_DOWNLOAD,
    CM_TYPE_WORKSPACE_UPDATE,
    CM_TYPE_LAYER_DELETED,
    CM_TYPE_CLIENT_NOTICE,
    CM_SSE_EVENT_TYPE
} from "@/engine/comm/sseCM.js";

class MapContext extends Evented {
    constructor(options) {
        super();
        this.options = options;
        this.options.mapViewAreaPadding = [40, 40]; //default padding when map is focused on the area
        //this.areas               = new Map();
        this.workspaces = new Map();
        this.sseCM = null;
        this.map = null; //leaflet object
        this.id = null; // userspace id
        this.baseMapLayers = {};
        this.identity = null;
        this.globalWorkspace = null;
        this.mapViewContext = null;
        this.drawingStyleContext = null;
        this.UIContext = null;
        this.mapUI = null;
        this.contextSaveInterval = 15 * 1000;
        this.mode = {
            oneWorkspace: false,
            observer: false,
            myLocation: false,
            zoom: true,
            move: true,
            iframe: typeof UA === 'undefined' ? false : UA.isIframe,
        }
        this.initialLocationFromUrl = null;
        this.debug = options.debugConsoleLog
        this.debugObjects = {}; // service container for runtime objects inspected from the browser console
        this.workspaceBoundsLG = L.featureGroup() // layer where workspace bounds rectangles is located
        this.selectRectLG = L.featureGroup() // layer where selected rectangle is created and controlled

        //logging facility
        this.elog = (typeof elog === "function") ? elog : function (severity, message) {
            if (typeof console[severity.toLowerCase()] === "function") {
                console[severity.toLowerCase()](message);
            }
        };

        //indexed db storage
        this.storage = new IDB("maphubmap");
        sm.setStorageWriter(this.storage.storeMessage.bind(this.storage))

        //define logging function
        this.elog = (typeof elog === "function") ? elog : function (severity, message) {
            if (typeof console[severity.toLowerCase()] === "function") {
                console[severity.toLowerCase()](message);
            }
        };

        this.stat = (options.devMode && typeof statCollector == "function") ? new statCollector(
            "summary",
            {
                apiUpdatesSent: 0,
                apiUpdatesReceived: 0,
                areasTotal: 0,
                SSEChannelControl: function () {
                    if (this.sseCM)
                        return this.sseCM.isAlive() ? "alive" : "died";
                    return "died";
                }.bind(this),
                controlUpdates: 0
            }) : null;

        //initial location coordinates
        this.defaultLocation = {
            latitude: 0,
            longitude: 0,
            accuracy: 1000,
            zoom: 10
        };

        if (options.location) {
            this.defaultLocation.longitude = options.location.longitude;
            this.defaultLocation.latitude = options.location.latitude;
            this.defaultLocation.accuracy = options.location.accuracy;
            this.defaultLocation.age = options.location.age;
        }

        // defined map state
        this.mode = optionsFromBrowserUrl(this.mode);
        this.initialLocationFromUrl = locationLoadFromBrowserUrl();
        let errorFromUrl = this._errorFromBrowserUrl();
        this.shareToken = this._shareTokenFromBrowserUrl();
        let scrollWheelZoom = !this.mode.iframe && this.mode.zoom;
        let attribution = null;
        if (this.mode.iframe) {
            this.mode.observer = true;
            this.options.mapViewAreaPadding = [10, 10];
            attribution = L.control.attribution({prefix: "Created with <b>maphub.online</b>"});
        }

        if (this.mode.observer && this.shareToken)
            this.mode.oneWorkspace = true;

        if (options.mapbox_access_token) {
            this.baseMapLayers["sat"] = L.tileLayer('https://api.mapbox.com/styles/v1/wormix/ckpi6w95c0kng17qvw772my6x/tiles/{z}/{x}/{y}?access_token=' + options.mapbox_access_token, {
                maxZoom: 24,
                maxNativeZoom: 18,
                className: 'baseTileLayer'
            });
            this.baseMapLayers["topo"] = L.tileLayer('https://api.mapbox.com/styles/v1/wormix/cllw4q8se00fx01nz70gtd7ey/tiles/{z}/{x}/{y}?access_token=' + options.mapbox_access_token, {
                maxZoom: 24,
                maxNativeZoom: 18,
                className: 'baseTileLayer'
            });
        }
        this.baseMapLayers["osm"] = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 24,
            maxNativeZoom: 19,
            className: 'baseTileLayer'
        })

        // initiate map
        this.map = L.map('map', {
            contextmenu: true,
            contextmenuWidth: 160,
            contextmenuItems: [],
            zoomControl: false,
            worldCopyJump: false,
            editable: true,
            attributionControl: false,
            scrollWheelZoom: scrollWheelZoom,
            doubleClickZoom: this.mode.zoom,
            dragging: this.mode.move
        });

        if (attribution) {
            attribution.addTo(this.map)
        }

        if (typeof L.imagePdf !== "undefined")  {
            this.pdf = new L.imagePdf(this.map, {
                excludeNodesWithCSS: [
                    'div.leaflet-control-container', // leaflet UI components
                    'div.control-pane-wrapper',      // leaflet UI components (sidebar?)
                    'div.pdf-progress-plug',         // pdf progress splash screen
                    'div.leaflet-control-window-wrapper'  // dialogs
                ],
                rectanglePreviewStyle: {
                    stroke: true,
                    weight: 1,
                    opacity: 1,
                    color: "darkslategrey",
                    fillColor: "grey",
                    fillOpacity: 0.2
                },
                progressSplashScreenStyle: {"z-index": 5000},
                downloadOnFinish: true,
                pdfDocumentProperties: {
                    'creator': "maphub.online",
                    'title': "Map region",
                    //'author': (mapContext.identity) ? mapContext.identity.firstName + ' ' + mapContext.identity.secondName : 'anonymous'
                },
                pdfFontSize: 12,
                pdfSheetAttribution: {      // add attribution text to a sheet at defined position
                    position: "bottomleft",
                    text: "created with maphub.online"
                },
                tilesLoadingTimeout: 90000,
                //debug: true
            })
        }

        // gps state machine
        this.gpsStateMachine = new gpsStateMachine({
            requiredAccuracy: 100,
            map: this.map,
        })

        if (eval(`typeof MapUI !== 'undefined'`)) {
            //initiate mapControlUI - the main UI contoller
            this.mapUI = new MapUI(this, {
                devMode: options.devMode,
                debugButtons: options.debugButtons,
                drawing: !this.mode.observer,
                sidebar: !this.mode.observer,
                signin: !this.mode.observer,
                messagesBox: !this.mode.observer,
                controls: !this.mode.observer,
                workspaces: !this.mode.observer,
                zoom: this.mode.zoom
            });

            // define where to display inapp messages
            sm.setDisplay(this.mapUI.messagesBox);

            if (errorFromUrl) {
                this.mapUI.UIDialogFactory.modalInfoWindow({
                    title: "Error is happened",
                    content: errorFromUrl,
                    closeButtonName: "OK",
                })
            }
        }

        // load and initialize mapViewContext
        // (where the current map view is pointing at the world map)
        if ( this.mode.observer ) {
            this._initMapView(this.defaultLocation)
        } else {
            this.mapViewContext = new appContext({
                name: "mapView",
                data: {
                    latitude: 0,
                    longitude: 0,
                    zoom: 11,
                    layer: ""
                },
                contextSaveInterval: this.contextSaveInterval,
                storage: this.storage,
                onLoadSucc: function (d) {
                    this._initMapView(d)
                }.bind(this),
                onLoadFailed: function () {
                    this._initMapView(this.defaultLocation)
                }.bind(this)
            });
        }

        // drawing style context stored in local storage
        this.drawingStyleContext      = new appContext({
            name: "drawingStyle",
            data: {  //default draw style for editable vector layers
                    opacity: 1,
                    color: '#3388ff',
                    weight: 2,
                    fillColor: '#3388ff',
                    fillOpacity: 0.2,
                    dashArray: '1'
            },
            contextSaveInterval: this.contextSaveInterval,
            storage: this.storage,
            onLoadSucc: function (d) {
                if (this.mapUI)
                    this.mapUI.setDrawingDefaultStyle(d)
            }.bind(this),
            onLoadFailed: function (d) {
                if (this.mapUI)
                    this.mapUI.setDrawingDefaultStyle(d)
            }.bind(this)
        });

        // application UI context stored in local storage
        if (! this.mode.observer)
            this.UIContext      = new appContext({
                name: "UI",
                data: {
                    myLocationMode: false,
                    showPublicMaps: true,
                },
                contextSaveInterval: this.contextSaveInterval,
                storage: this.storage,
                onLoadSucc: function (d) {
                    // migration
                    if (d.showPublicMaps === undefined) {
                        d.showPublicMaps = true;
                    }
                }.bind(this),
                onLoadFailed: function (d) {

                }.bind(this),
                onValueChange: function (name, val) {
                    switch (name) {
                        case "myLocationMode":
                            this.mode.myLocation = val
                            if (val) {
                                this.startLocator();
                            } else {
                                this.stopLocator();
                            }
                            break
                        case "showPublicMaps":
                            if (val) {
                                for (let ws of this.getWorkspacesPublic()) {
                                    let visible = this.isWorkspaceVisibleInUI(ws)
                                    if (visible)
                                        ws.show()

                                    for (let layer of ws.getLayers()) {
                                        let visible = this.isLayerVisibleInUI(layer)
                                        if (visible)
                                            layer.show()
                                    }
                                }
                            } else {
                                for (let ws of this.getWorkspacesPublic()) {
                                    ws.hide()
                                }
                            }
                            break
                    }
                }.bind(this),
                debug: true
            });

        //save current map position to the context
        if (this.mapViewContext) {
            this.map.on('moveend', function (ev) {
                let center = ev.target.getCenter()
                this.mapViewContext.set({"latitude": center.lat, "longitude": center.lng});
                updateBrowserUrl(this)
            }.bind(this));

            this.map.on('zoomend', function (ev) {
                this.mapViewContext.set({"zoom": ev.target.getZoom()})
                updateBrowserUrl(this)
            }.bind(this));
        }

        //process geometry creating event from geoman editing library
        this.map.on('pm:create', this.createGeometry.bind(this));
        //turns on deffered transactions when global editing mode is enabled
        this.map.on('pm:globaleditmodetoggled', function (e) {
            e.enabled ? this.geometryModificationTransactionStart() : this.geometryModificationTransactionStop()
        }.bind(this));

        //init region select box features
        this.selectRectLG.on("layeradd", function (ev) {
            console.log("select rect has been created")
        }).addTo(this.map)

        this.workspaceBoundsLG.on("layeradd", function (ev) {

        }).addTo(this.map)
    }

    //registers RPC handlers
    _registerSSEHandlers() {

        let lastProgressNoticeMessage = null
        this.sseCM.registerMessageHanlder(CM_TYPE_PROGRESS, function (msg) {
            let t = msg.payload;
            //console.log(`CM_TYPE_PROGRESS ${msg.uid} `)
            if (t.done) {
                lastProgressNoticeMessage = null
                if (t.success) {
                    sm.noticeMessage(`finished successfully`);
                } else {
                    let errMsg = (t.errorPublicMessage !== "") ? t.errorPublicMessage : t.errorMessage

                    if (t.errorCode === CM_ERROR_FORMAT) {
                        errMsg = "unsupported or invalid file: " + errMsg
                    } else if (t.errorCode === CM_ERROR_DOWNLOAD) {
                        errMsg = "can't download the file"
                    }
                    if (t.errorDetail && Array.isArray(t.errorDetail)) {
                        for (let l of t.errorDetail) {
                            sm.noticeMessage(l);
                        }
                    }
                    sm.noticeMessage(`operation failed: ${errMsg}`);
                }
            } else {
                let text = `${t.operation}${(t.operation !== "START")?' ' + t.progress+'%':""}`;
                // it needs to update a previous messages line instead inserting a new one
                // on each progress event
                if (lastProgressNoticeMessage == null || t.operation === "START") {
                    lastProgressNoticeMessage = sm.noticeMessage(text);
                } else {
                    lastProgressNoticeMessage.payload.text = text;
                    sm.replaceMessage(lastProgressNoticeMessage, lastProgressNoticeMessage.uid)
                }
            }
        })

        this.sseCM.registerMessageHanlder([CM_TYPE_LAYER_CREATED], async function (msg) {
            let layerStruct = msg.payload;

            try {
                let workspace = this.getWorkspace(layerStruct.workspaceId);
                let geoData = ""
                if (! workspace) {
                    workspace = this.createWorkspace(await mapApi.apiGetWorkspace(layerStruct.workspaceId))
                    workspace.addOnMap(this.map)
                    this.fire("mh:wsCreated", {workspace: workspace})
                    geoData = workspace.geoCodeHuman
                } else {
                    workspace.addLayer(layerStruct)
                }

                if (geoData !== '') {
                    geoData = ' (' + geoData + ')'
                }
                sm.noticeMessage(`A new ${layerStruct.type === LAYER_TYPE_RASTER ? 'map' : 'layer'} ${layerStruct.name}${geoData} has been created`)

                this.mapViewTo(workspace.getLayer(layerStruct.id))
            } catch (e) {
                elog("WARN", "can't create a layer object", e)
            }

        }.bind(this))

        this.sseCM.registerMessageHanlder([CM_TYPE_LAYER_UPDATE], async function (msg) {
            // reloads the layer when some major layer options were changed by backend
            // for example CRS or Bounds or layer type
            let layerStruct = msg.payload;

            try {
                let workspace = this.getWorkspace(layerStruct.workspaceId);
                let l = workspace.recreateLayer(layerStruct)
                this.mapViewTo(workspace.getLayer(layerStruct.id))
                this.fire("cm:layerUpdate", {layer: l})
            } catch (e) {
                elog("WARN", "can't create a layer object", e)
            }
        }.bind(this))

        this.sseCM.registerMessageHanlder([CM_TYPE_WORKSPACE_CREATED], async function (msg) {
            // isn't implemented yet
            let wsStruct = msg.payload;

            let workspace = this.getWorkspace(wsStruct.id);
            if (workspace) {
                // already exists
            }
        }.bind(this))

        this.sseCM.registerMessageHanlder([CM_TYPE_WORKSPACE_UPDATE], async function (msg) {
            let wsStruct = msg.payload;

            try {
                let workspace = this.getWorkspace(wsStruct.id);
                workspace.name = wsStruct.name;
                workspace.geodata = wsStruct.geodata;
                //workspace.preview = wsStruct.preview;
                workspace.initBounds(wsStruct.bounds)
            } catch (e) {
                elog("WARN", "can't create a layer object", e)
            }
        }.bind(this))

        this.sseCM.registerMessageHanlder([CM_TYPE_LAYER_DELETED], async function (msg) {
            try {
                sm.noticeMessage(`Layer has been deleted`)
            } catch (e) {

            }

        }.bind(this))


        this.sseCM.registerMessageHanlder(CM_TYPE_WORKSPACE_DELETED, async function (msg) {
            let workspaceId = msg.payload;

            try {
                let workspace = this.getWorkspace(workspaceId)
                if (workspace != null) {
                    this._removeWorkspace(workspace)
                }
            } catch (e) {
                elog("WARN", "can't delete the workspace object", e)
            }
        }.bind(this))
    }

    /**
     * creates geometry and performs geometry registration and saving
     * @param pmLayer - leaflet layer or created geometry (geoman object)
     * @param interactive
     */
    createGeometry(pmLayer, interactive = true, workspace) {
        let layer = (pmLayer.layer !== undefined) ? pmLayer.layer : pmLayer
        // if this is selection rectangle
        if (this.selectRectLG.hasLayer(layer)) {
            return
        }

        if (typeof AMUtil !== 'undefined')
            AMUtil.fixLayer(layer)

        //determining the layer (workspace) this geometry belongs to according position this geometry is created at
        let layerBounds = (typeof layer.getBounds == "function") ? layer.getBounds() : layer.getLatLng().toBounds(1);

        //iterate over our workspaces/areas
        if (! workspace) {
            let ws = this.getWorkspacesVisibleWritable();
            for (let i in ws) {
                let b = ws[i].getBounds()
                if (b && layerBounds.overlaps(ws[i].getBounds())) {
                    workspace = ws[i];
                    break
                }
            }
        }

        if (!workspace) {
            this.elog("DEBUG", "Can't determine the area new geometry belongs to, the globalWorkspace is used")
            workspace = this.globalWorkspace;
        }
        this.elog("DEBUG", `New geometry is distributed to workspace ${workspace.id}`)

        let done = false;
        // show dialog to set marker description
        if ( layer instanceof L.Marker && interactive ) {
            // disable draw mode to prevent massive marker creating while dialog is on the screen
            this.map.pm.disableDraw()
            if (this.mapUI)
                this.mapUI.createEditGeometryPropertiesWindowDialog(
                    layer, function (prop) {
                    done = workspace.addFeature(layer, prop)
                    }, function () {

                    }
                )
        } else {
            let prop = {}
            // load properties from feature property (is set by some layer exporters, like geoJson)
            if (layer.feature !== undefined && layer.feature.properties !== undefined) {
                let name = layer.feature.properties.name || layer.feature.properties.Name
                if (name)
                    prop.descr = name
                // options contains our style options (can be set in json file by our geoJson exporter)
                /*
                if (layer.feature.properties.options)
                    prop.options = layer.feature.properties.options
                 */
            }
            done = workspace.addFeature(layer, prop)
        }
        if (! done) {
            layer.remove()
        } else {
            return workspace
        }
    }

    // is used on bulk geometry operations
    geometryModificationTransactionStart() {
        this._geometryModificationTransaction(true)
    }

    geometryModificationTransactionStop() {
        this._geometryModificationTransaction(false)
    }

    _geometryModificationTransaction(enabled) {
        let workspaces = this.getWorkspacesWritable()
        if (this.globalWorkspace)
            workspaces.push(this.globalWorkspace)
        for (let i in workspaces) {
            let vectorLayers = workspaces[i].getVectorLayers()
            for (let j in vectorLayers) {
                if (typeof vectorLayers[j].setCommitMode === "function")
                    vectorLayers[j].setCommitMode(enabled);
            }
        }
    }
    /**
     * creates app's workspace instance by workspaceStruct
     * @param workspaceStruct - workspace description
     * @returns {Workspace}
     */
    createWorkspace(workspaceStruct) {
        if (! workspaceStruct) {
            elog("empty workspace struct on createWorkspace")
            return null
        }
        let ws = new Workspace(Object.assign({}, workspaceStruct, {debug: this.debug}))
        this._addWorkspace(ws)

        if (ws.mine && workspaceStruct.global) {
            if (this.globalWorkspace != null) {
                elog("global workspace already defined, do we have two global workspaces ?")
            } else {
                this.globalWorkspace = ws
            }
        }
        return ws
    }

    /**
     * creates new workspace with bounds
     * @param bounds
     */
    createNewWorkspace(bounds) {
        let coveredFeatures = this.getFeaturesCoveredWithBounds(bounds)
        elog("DEBUG", `Total ${coveredFeatures.length} features covered by a select region`)
        this.RPCCreateWorkspace(null, bounds)
        this.once("mh:wsCreated", function (data) {
            if (! data.workspace)
                return

            if (coveredFeatures.length > 0) {
                this.globalWorkspace.moveFeaturesToWorkspace(coveredFeatures, data.workspace)
            }
            this.mapUI.workspaceBounds.get(data.workspace.id).show()
        }.bind(this))
    }

    getFeaturesCoveredWithBounds(bounds) {
        let vls = this.globalWorkspace.getVectorLayers()
        let coveredFeatures = []
        for (let i in vls) {
            coveredFeatures.push(...geometryUtils.layersCoveredWithBound(bounds, vls[i].layer.getLayers()))
        }
        return coveredFeatures
    }
    /**
     * RPCUploadEntity starts uploading and onboarding workflow for a geometry entity pointed by uri
     * @param uploadMethod - "file" or "url"
     * @param uploadType - "geotiff" or "shape"
     * @param uri - file object or url string where the entity should be downloaded from
     * @param workspaceId - workspace the area will belong to, if null the new workspace will be created
     */
    RPCUploadEntity(uploadMethod, uploadType, uri, workspaceId) {
        let uploadWorker = function (uri, workspaceId) {
            sm.noticeMessage("Creating a new map area in progress...");
            mapApi.apiCreateLayer(uri, uploadType, workspaceId).then(function (result) {
                this._logdebug(`Onboarding has been started with task id ${result.data.TaskId}`)
            }.bind(this), function (error) {
                sm.noticeMessage("Operation is failed. Internal server error, try later");
            });
        }.bind(this)

        this.mapUI.messagesBox.show()
        if (uploadMethod === "file") {
            sm.noticeMessage(`Uploading file ${uri.name}...`);
            let lastProgressNoticeMessage = null;
            let errMsg = "Internal error. Try 5 minutes later";
            mapApi.fileUpload(
                "onboarding",
                uri,
                function (bytesUploaded, bytesTotal, percentage) {
                    let text = `UPLOADING ${percentage}%`;
                    if (lastProgressNoticeMessage == null) {
                        lastProgressNoticeMessage = sm.noticeMessage(text);
                    } else {
                        lastProgressNoticeMessage.payload.text = text;
                        sm.replaceMessage(lastProgressNoticeMessage, lastProgressNoticeMessage.uid)
                    }
                }.bind(this),
                function (uploadId) {
                    uploadWorker(uploadId, workspaceId)
                }.bind(this),
                function (error) {
                    if (typeof error === "string")
                        errMsg = error;
                    sm.noticeMessage(`uploading failed: ${errMsg}`);
                }.bind(this));
        } else if (uploadMethod === "url") {
            uploadWorker(uri, workspaceId)
        }
    }

    /**
     * creates workspace
     * @constructor
     * @param name
     * @param bounds
     */
    RPCCreateWorkspace(name, bounds) {
        mapApi.apiCreateWorkspace(name, bounds).then(async function (workspace) {
            workspace = this.createWorkspace(workspace)
            workspace.addOnMap(this.map)
            this._logdebug(`Workspace ${workspace.id} has been created`)
            this.fire("mh:wsCreated", {workspace: workspace})
        }.bind(this), function (error) {
            sm.noticeMessage(`Can't create new workspace: ${error.message}`);
            // it needs in order the createNewWorkspace method finish
            // the flow correctly
            this.fire("mh:wsCreated")
        }.bind(this));
    }

    /**
     * deletes area
     * @param area
     * @constructor
     */
    RPCDeleteWorkspace(workspace) {
        mapApi.apiDeleteWorkspace(workspace).then(function (result) {
            this._removeWorkspace(workspace)
            //sm.noticeMessage(`Workspace ${workspace.name} has been deleted`)
        }.bind(this), function (error) {
            sm.noticeMessage(`Can't delete the workspace: ${error.message}`);
        });
    }

    RPCDeleteLayer(layer) {
        mapApi.apiDeleteLayer(layer).then(function (result) {
            layer.workspace.removeLayer(layer)
        }, function (error) {
            sm.noticeMessage(`Can't delete the layer: ${error.message}`);
        });
    }

    RPCUpdateProperties(obj, propertiesList = null) {
        let updater = null
        if (obj instanceof Workspace) {
            updater = mapApi.apiUpdateWorkspace.bind(mapApi)
        } else {
            updater = mapApi.apiUpdateLayer.bind(mapApi)
        }
        updater(obj, propertiesList).then(function (result) {

        }, function (error) {
            sm.noticeMessage("Can't update properties. Internal server error, try later");
        });
    }

    //
    _addWorkspace(ws) {
        this.stat.add("workspacesTotal");
        this.workspaces.set(ws.id, ws);
        let wsBounds = new UIWorkspaceBounds(ws, this.map, this.workspaceBoundsLG)
        if (this.mapUI)
            this.mapUI.workspaceBounds.set(ws.id, wsBounds)
        ws.on("visiblechanged", function () {
            wsBounds.updateBoundsVisibility()
        })
    }

    _removeWorkspace(ws) {
        if (ws == null)
            return
        this.stat.dec("workspacesTotal");
        this.workspaces.delete(ws.id);
        ws.removeFromMap();
        if (this.mapUI)
            this.mapUI.workspaceBounds.delete(ws.id)
        this.fire("mh:wsDelete", {workspace: ws})
    }

    getWorkspace(id) {
        return this.workspaces.get(id);
    }
    /**
     * getWorkspaces returns workspaces list except special global workspace
     * @returns {*[]}
     */
    getWorkspaces() {
        let o = []
        for (let obj of this.workspaces.values()) {
            if (obj !== this.globalWorkspace) {
                o.push(obj)
            }
        }
        return o;
    }

    getWorkspacesVisible() {
        return this._getWorkspacesFiltered(["visible"])
    }

    getWorkspacesHidden() {
        return this._getWorkspacesFiltered(["visible", "!"])
    }

    getWorkspacesWritable() {
        return this._getWorkspacesFiltered(["writable"])
    }

    getWorkspacesVisibleWritable() {
        return this._getWorkspacesFiltered(["writable", "visible" , "A"])
    }

    getWorkspacesVisibleRealWritable() {
        return this._getWorkspacesFiltered(["writable", "isVisibleReal" , "A"])
    }

    // returns
    getWorkspacesPublic() {
        // ! mine && public && bounds != null
        return this._getWorkspacesFiltered(["public", "bounds", "mine", "!", "A", "A"])
    }

    // returns private workspaces
    // orderArray is optional, contains ordered list of workspaces ids
    // and is used when the result should be ordered
    getWorkspacesPrivate(orderArray) {
        // ! public || mine || writable
        let l = this._getWorkspacesFiltered(["public", "!","mine", "writable", "O", "O"])
        if (! orderArray ||
            ! (orderArray instanceof Array) ||
            orderArray.length === 0)
            return l

        let ul = []
        let ol = []
        for (let id of orderArray) {
            for (let ws of l) {
                if (ws.id === id)
                    ol.push(ws)
            }
        }
        if (ol.length === l.length)
            return ol

        // some ws isn't in the ordered array,
        // find and place them at the top
        for (let w of l) {
            if (! ol.includes(w)) {
                ul.push(w)
            }
        }
        ol.unshift(...ul)

        return ol
    }

    getWorkspacesMine() {
        return this._getWorkspacesFiltered(["mine"])
    }

    /**
     * @param filterExpr {array}
     * @returns {*[]}
     * @private
     */
    _getWorkspacesFiltered(filterExpr) {
        return gcUtils.objFilter(Array.from(this.getWorkspaces()), filterExpr)
    }

    getBounds(onlyVisible) {
        return gcUtils.bounds(onlyVisible, Array.from(this.getWorkspaces()))
    }

    mapViewTo(object, zoom, animate) {
        if (object == null) {
            elog("ERROR", "object is null")
            return
        }

        if (! object.isVisible())
            return

        if ( object.srs !== 'undefined' && (object.srs === "" || object.srs === "EPSG:404000")) {
            sm.noticeMessage(`can't georeference the layer '${object.name}' cause incorrect layer CRS`)
            return
        }

        if (object.getBounds === 'undefined') {
            elog("ERROR", "bounds method isn't defined for object")
            return
        }

        let b = object.getBounds()
        if (! b) {
            //elog("ERROR", "bounds is null for object")
            sm.noticeMessage(`can't display the layer '${object.name}' due to incorrect layer coordinates, possibly because of an incorrectly specified layer CRS`)
            return
        }

        // close sidebar if it takes more 50% of viewport
        if (this.mapUI &&
            this.mapUI.sidebar &&
            window.visualViewport.width / mapContext.mapUI.sidebar._container.clientWidth < 1)
                this.mapUI.sidebar.close()
        //console.warn('mapViewToArea', area.getBounds())
        this.map.fitBounds(object.getBounds(), {maxZoom: zoom ? zoom : 20, padding: this.options.mapViewAreaPadding, animate: animate});
    }

    //position the map to LatLng in center
    //call map setView
    //do coords translation to fix antimeridian bug
    setView(location, zoom) {
        //antimeridian fix if antimeridianUtils is loaded
        if (typeof AMUtil !== 'undefined')
            AMUtil.fixLng(location)
        //console.warn('mapSetView', location)
        this.map.setView(location, zoom);
    }

    /**
     * set base background map layer (openstreetmaps, mapbox, etc)
     * @param name
     */
    mapSetLayer(name) {
        if (!this.baseMapLayers[name])
            return
        for (let k of Object.keys(this.baseMapLayers)) {
            if (k !== name && this.map.hasLayer(this.baseMapLayers[k])) {
                this.map.removeLayer(this.baseMapLayers[k])
            }
        }
        if (!this.map.hasLayer(this.baseMapLayers[name])) {
            this.map.addLayer(this.baseMapLayers[name])
        }
    }

    mapSetNextLayer() {
        //todo do the refactoring with using mapSetLayer
        let rm = false;
        let layers = Object.keys(this.baseMapLayers);
        for (let k of layers) {
            if (this.map.hasLayer(this.baseMapLayers[k])) {
                this.map.removeLayer(this.baseMapLayers[k])
                rm = true;
                continue;
            }
            if (rm) {
                this.map.addLayer(this.baseMapLayers[k])
                if (this.mapViewContext)
                    this.mapViewContext.set({"layer": k});
                rm = false;
                break;
            }
        }
        if (rm) {
            this.map.addLayer(this.baseMapLayers[layers[0]]);
            if (this.mapViewContext)
                this.mapViewContext.set({"layer": layers[0]});
        }
    }

    mapViewToObserveAllWorkspaces() {
        let bounds= gcUtils.bounds(true, this._getWorkspacesFiltered(["mine", "visible", "A"]))
        if (bounds) {
            this.map.flyToBounds(bounds, {padding: [40,40]})
        } else {
            elog("mapViewToObserveAllAreas: can't evaluate the bounds")
        }
    }

    /**
     * _errorFromBrowserUrl checks for error flags in browser url,
     *  ory/kratos redirects to this url when error is happened in openAuth process (google or facebook auth)
     * @returns {string} string describing the error or null
     * @private
     */
    _errorFromBrowserUrl() {
        let u = new URL(window.location);
        if (u.searchParams.get("kratosError") != null ) {
            let id = u.searchParams.get("id");
            this.elog("ERROR", "Kratos error is happened during OID auth", {errorId: id});
            //todo here we can request error details, and display to the user error details
            //let errDetails = identity.getError(id)
            return "Can't finish sign in process. Try again please."
        }
        return null
    }

    _shareTokenFromBrowserUrl() {
        let u = new URL(window.location);
        if (! /\/[0-9]{3}-[0-9]{6}-[0-9]{3}$/.test(u.pathname))
            return null
        return u.pathname.substring(u.pathname.length-14)
    }

    signIn() {
        identity.doLoginOID("google");
    }

    signOut() {
        identity.doLogout().then(r => {
            if (r) {
                mapApi.deleteCookie("mapperS");
                location.href = "/";
            } else {
                sm.notice("got some internal error while logout")
            }
        })
    }

    startLocator() {
        if (! this.mode.myLocation)
            return;

        elog("gps location watching has been started");
        //do the highAccuracy request

        this.gpsStateMachine.start();
    }

    stopLocator(){
        this.gpsStateMachine.stop();
    }

    async run() {
        sm.silent = false;
        profiling("init", "run is started")
        let userspace
        let apiData
        try {
            let usOpt = {};
            // it's map sharing web link,
            // add token to userspace request to activate them
            if (this.shareToken) {
                usOpt.activateShareToken = this.shareToken
            }
            // this request should be done at first to init session keys and cookies
            apiData = await mapApi.apiGetUserspace(usOpt);
            userspace = apiData.data
            shortLivedTokenManager.start(userspace.shortLivedToken);
            profiling("init", "userspace is ready");
            this.id = userspace.id;
            log(`Userspace ${userspace.id} loaded with ${userspace.workspaces.length} workspaces`);
            this.identity = userspace.identity;
            //todo refactor, should assign only closed list of options
            if (userspace.options) {
                Object.assign(this.options, userspace.options);
            }

            let createGlobalWorkspaceIfNeeded = function () {
                if ( ! this.mode.observer && ! this.mode.oneWorkspace && this.globalWorkspace == null) {
                    if (! this.options.newOne)
                        elog("ERROR", "Something get wrong, there are no global workspace. Trying to create it")
                    this.RPCCreateWorkspace()
                }
            }.bind(this)

            if (this.options.newOne || ! this.UIContext) {
                // to speed up app init for new user or when UIContext isn't defined
                // don't wait UIContext as it's empty anyway
                this._initWorkspaces(userspace.workspaces)
                // create default workspace if needed
                createGlobalWorkspaceIfNeeded()
            } else {
                this.UIContext.getReady( function (ok) {
                    profiling("init", "UIcontext is ready");
                    this._initWorkspaces(userspace.workspaces)
                    createGlobalWorkspaceIfNeeded()
                }.bind(this))
            }

            if (this.mapUI)
                this.mapUI.signInOutUIUpdate()

            // disabled
            /*
            if (this.mapUI.signinPane)
                this.mapUI.signinPane.update();
             */

            profiling("init", "signing pane is updated");
        } catch (e) {
            // it's an emergency, like wrong network link or internal server error or user account blocking
            // todo need to add additional processing and ui messaging
            let msg = `mapContext: can't initialize the session: ${e.message}` + (e.detail ? ", " + e.detail : "");
            elog("WARN", msg, e);
            if (e.code >= 500) {
                if (this.mapUI)
                    mapContext.mapUI.UIDialogFactory.modalInfoWindow({
                        content: "Currently, the service is undergoing maintenance and is temporarily unavailable. We are actively working on resolving the issue"
                    })
            }
            sm.serverError(e.code);
            if (this.mapUI)
                this.mapUI.signInOutUIUpdate()

            //disabled
            /*
            if (this.mapUI.signinPane)
                this.mapUI.signinPane.update();
             */
            return
        }

        if (!userspace || !userspace.workspaces ) {
            sm.notice("Can\'t initialize the session: internal server error");
            elog("WARN", "wrong userspace");
            return
        }

        if (apiData.code && apiData.code > 0) {
            // code 17 processing here
            // it's important that workspaces were initialized with this error
            // in order the observer mode and iframe map to work correctly
            sm.serverError(apiData.code)
            return
        }

        //create contolMessages sse channel
        this.sseCM = new sseCM("/v1/stream/control");
        this._registerSSEHandlers();
        profiling("init", "ready for sse ok")
    }

    isWorkspaceVisibleInUI(workspace) {
        if (! this.UIContext)
            return true
        let hiddenWorkspaces = this.UIContext.get("hidden-workspaces")
        let showPublicWorkspaces = this.UIContext.get("showPublicMaps")
        if (! workspace.mine && !workspace.shared && workspace.public && ! showPublicWorkspaces)
            return false

        if (hiddenWorkspaces instanceof Array) {
            for (let id of hiddenWorkspaces) {
                if (id === workspace.id) {
                    return false
                }
            }
        }
        return true
    }

    // todo need to implement
    isLayerVisibleInUI(layer) {
        if (! this.UIContext)
            return true

        let hiddenLayers = this.UIContext.get("hidden-layers")

        if (hiddenLayers instanceof Array) {
            for (let id of hiddenLayers) {
                if (id === layer.id) {
                    return false
                }
            }
        }
        return true
    }

    // initial initialization workspaces
    _initWorkspaces(workspaces) {
        profilingStart(`workspaces init`);
        //let hiddenWorkspaces = this.UIContext.get("hidden-workspaces")
        for (let i in workspaces) {
            let workspaceStruct = workspaces[i]
            if (this.mode.oneWorkspace && this.options.setView) {
                if (workspaceStruct.id !== this.options.setView) {
                    continue
                }
            }
            workspaceStruct.visible = this.isWorkspaceVisibleInUI(workspaceStruct)
            for (let layer of workspaceStruct.layers) {
                layer.visible = this.isLayerVisibleInUI(layer);
            }
            this.createWorkspace(workspaceStruct)
        }
        profiling("init", "workspaces is created");
        for (let workspace of this.workspaces.values()) {
            profilingStart(`${workspace.id} areas init`);
            // setView option contains the workspaceId and
            // is set when shared web link is opened
            // as result of userspace api request
            // here we set map view to the shared map
            if (this.initialLocationFromUrl == null &&
                this.options.setView &&
                workspace.id === this.options.setView) {
                this.mapViewTo(workspace, null, ! this.mode.oneWorkspace);
            }
            workspace.addOnMap(this.map)
        }
        this.fire("mh:initFinished")
        profilingFinish(`workspaces init`);
    }

    _initMapView(defaultMapView) {
        if (this.initialLocationFromUrl == null) {
            if (!this.map._loaded) {
                this.setView(L.latLng([defaultMapView.latitude, defaultMapView.longitude]), defaultMapView.zoom);
            }
        } else {
            this.setView(this.initialLocationFromUrl.location, this.initialLocationFromUrl.zoom);
        }
        if (defaultMapView.layer && defaultMapView.layer !== "") {
            this.mapSetLayer(defaultMapView.layer);
        } else {
            this.mapSetLayer("osm");
        }
    }
    _saveContext(name, obj) {
        this.storage.storeContext(name, obj)
    }

    _logdebug(msg) {
        if (this.debug)
            elog("DEBUG", msg)
    }

}

export {MapContext};
